import { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { UnifiedScanner, type ScannerMode } from '@/components/UnifiedScanner';
import { Barcodes, Wines, Bottles, Scan, Labels, type BarcodeResolveResult, type ScanResponse } from '@/lib/api';
import { Button, Card, EmptyState, Icon, Spinner, Skeleton, Badge, Tabs, Input, Modal } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';
import { AddBottleDrawer } from '@/components/AddBottleDrawer';
import { ScanResultCard, extractionToPrefill } from '@/components/ScanResultCard';
import { usePrefs, useToasts } from '@/lib/store';
import { formatAbv, formatPriceNok, formatVolumeMl } from '@/lib/format';
import type { BottleCreate } from '@/lib/api';

type ResultView =
  | { kind: 'idle' }
  | { kind: 'barcode'; data: BarcodeResolveResult }
  | { kind: 'photo'; data: ScanResponse }
  | { kind: 'error'; message: string };

type ConfirmBarcodeSelection = (productId: string, vintage?: string) => void;
type ConfirmProductSelection = (product: BarcodeProduct, vintage?: string) => void;
type BarcodeCandidate = BarcodeResolveResult['candidates'][number];
type PhotoCandidate = ScanResponse['candidates'][number];
type MatchCandidate = Pick<BarcodeCandidate, 'score' | 'matchedOn'>;
type BarcodeProduct = NonNullable<BarcodeResolveResult['product']>;
type BatchScanStatus = 'pending' | 'found' | 'not_found' | 'error' | 'accepted';

// Photo-scan stack item — shown as compact card directly under scanner
type PhotoStackItem = {
  id: string;
  status: 'scanning' | 'done' | 'error' | 'added';
  scanData?: ScanResponse;
  errorMessage?: string;
  addedBottleId?: string;
  previewUrl?: string;
};

type BatchScanItem = {
  barcode: string;
  status: BatchScanStatus;
  title: string;
  description?: string;
  product?: BarcodeProduct;
  data?: BarcodeResolveResult;
  error?: string;
  count: number;
  updatedAt: number;
};

export function ScanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { push } = useToasts();
  const { printTarget } = usePrefs();
  const [result, setResult] = useState<ResultView>({ kind: 'idle' });
  const [mode, setMode] = useState<ScannerMode>('auto');
  const [batchScans, setBatchScans] = useState<BatchScanItem[]>([]);
  const [barcodePendingCount, setBarcodePendingCount] = useState(0);
  const [acceptingBarcode, setAcceptingBarcode] = useState<string | null>(null);
  const [labelQueueEnabled, setLabelQueueEnabled] = useState(true);
  const resolvingCodesRef = useRef(new Set<string>());

  // Continuous photo-scan stack: each captured photo appears as a card
  const [photoStack, setPhotoStack] = useState<PhotoStackItem[]>([]);
  const [addingPhotoId, setAddingPhotoId] = useState<string | null>(null);
  // AddBottleDrawer for manual adds — also tracks which stack item triggered it
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPrefill, setDrawerPrefill] = useState<Partial<BottleCreate>>({});
  const [drawerSourceStackId, setDrawerSourceStackId] = useState<string | null>(null);
  const photoCounterRef = useRef(0);

  const scanLabel = useMutation({
    mutationFn: async ({ file, stackId }: { file: File; stackId: string }) => {
      const dataUrl = await fileToBase64(file);
      const base64 = dataUrl.split(',')[1] ?? dataUrl;
      const data = await Wines.scanLabel({ imageBase64: base64, mimeType: file.type, candidateLimit: 8 });
      return { data, stackId };
    },
    onSuccess: ({ data, stackId }) => {
      setResult({ kind: 'photo', data });
      setPhotoStack((prev) => prev.map((it) =>
        it.id === stackId ? { ...it, status: 'done', scanData: data } : it,
      ));
      const first = data.candidates[0]?.product;
      push(
        first
          ? { title: 'Fant etikettmatch', description: productDisplayName(first), tone: 'success' }
          : { title: 'Fant ingen etikettmatch', description: data.extraction.searchQuery || 'Prøv et tydeligere bilde.', tone: 'danger' },
      );
    },
    onError: (err: Error, { stackId }) => {
      setResult({ kind: 'error', message: err.message });
      setPhotoStack((prev) => prev.map((it) =>
        it.id === stackId ? { ...it, status: 'error', errorMessage: err.message } : it,
      ));
      push({ title: 'Etikettskann feilet', description: err.message, tone: 'danger' });
    },
  });

  const createBottleFromProduct = async (product: BarcodeProduct, options: { barcode?: string; vintage?: string; qty?: number } = {}) => {
    const p = product;
    const country = (p.origins as { country?: string } | undefined)?.country;
    const region = (p.origins as { region?: string } | undefined)?.region;
    const vintageOverride = options.vintage?.trim();
    const requestedQty = Math.trunc(Number(options.qty ?? 1));
    const qty = Number.isFinite(requestedQty) && requestedQty > 0 ? requestedQty : 1;
    return Bottles.create({
      productId: p.basic.productId,
      catalogId: p.basic.productId,
      name: p.basic.productLongName ?? p.basic.productShortName ?? 'Ukjent',
      vintage: vintageOverride || p.basic.vintage,
      country,
      region,
      photoUrl: p.imageUrl,
      barcode: options.barcode,
      qty,
    });
  };

  const createBottleFromBarcode = async ({ barcode, productId, vintage, qty }: { barcode: string; productId: string; vintage?: string; qty?: number }) => {
    const mappingResp = await Barcodes.confirm(barcode, { productId, source: 'web-scan' });
    const bottle = await createBottleFromProduct(mappingResp.product, { barcode, vintage, qty });
    return { mapping: mappingResp, bottle };
  };

  const queueQrLabelsForBottles = async (bottleIds: string[]) => {
    if (!labelQueueEnabled || bottleIds.length === 0) return false;
    try {
      await Labels.printBottles({
        bottleIds,
        includeQr: true,
        includeBarcode: true,
        printerName: printTarget?.printerName,
        deliveryTarget: printTarget ?? undefined,
      });
      void qc.invalidateQueries({ queryKey: ['labels.queue'] });
      void qc.invalidateQueries({ queryKey: ['labels.list'] });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      push({ title: 'Etikettkø feilet', description: message, tone: 'danger' });
      return false;
    }
  };

  const confirmBarcode = useMutation({
    mutationFn: async ({ barcode, productId, vintage }: { barcode: string; productId: string; vintage?: string }) => {
      const data = await createBottleFromBarcode({ barcode, productId, vintage });
      const labelQueued = await queueQrLabelsForBottles([data.bottle.id]);
      return { ...data, labelQueued };
    },
    onSuccess: (data) => {
      push({
        title: data.labelQueued ? 'Lagt i kjelleren og etikettkø' : 'Lagt i kjelleren',
        description: data.bottle.name,
        tone: 'success',
      });
      navigate(`/bottle/${data.bottle.id}`);
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke bekrefte', description: err.message, tone: 'danger' }),
  });

  const confirmPhotoCandidate = useMutation({
    mutationFn: async ({ product, vintage }: { product: BarcodeProduct; vintage?: string }) => {
      const bottle = await createBottleFromProduct(product, { vintage });
      const labelQueued = await queueQrLabelsForBottles([bottle.id]);
      return { bottle, labelQueued };
    },
    onSuccess: ({ bottle, labelQueued }) => {
      push({
        title: labelQueued ? 'Lagt i kjelleren og etikettkø' : 'Lagt i kjelleren',
        description: bottle.name,
        tone: 'success',
      });
      navigate(`/bottle/${bottle.id}`);
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke legge til etikettmatch', description: err.message, tone: 'danger' }),
  });

  const upsertBatchScan = (item: BatchScanItem, options: { incrementCount?: boolean } = {}) => {
    setBatchScans((current) => {
      const existing = current.find((entry) => entry.barcode === item.barcode);
      if (!existing) return [item, ...current].slice(0, 40);
      const count = options.incrementCount
        ? existing.count + Math.max(1, item.count)
        : existing.count;
      return [
        { ...existing, ...item, count, updatedAt: Date.now() },
        ...current.filter((entry) => entry.barcode !== item.barcode),
      ];
    });
  };

  const onCode = async (code: string) => {
    const barcode = code.trim();
    if (!barcode || resolvingCodesRef.current.has(barcode)) return;

    resolvingCodesRef.current.add(barcode);
    setBarcodePendingCount((count) => count + 1);
    upsertBatchScan({
      barcode,
      status: 'pending',
      title: 'Slår opp strekkode',
      description: barcode,
      count: 1,
      updatedAt: Date.now(),
    }, { incrementCount: true });

    try {
      const { result: data } = await Scan.barcode(barcode, { candidateLimit: 8 });
      setResult({ kind: 'barcode', data });
      const summary = summarizeBarcodeResult(data);
      upsertBatchScan({
        barcode,
        status: summary.product ? 'found' : 'not_found',
        title: summary.title,
        description: summary.description,
        product: summary.product,
        data,
        count: 1,
        updatedAt: Date.now(),
      });
      push(
        summary.product
          ? { title: 'Fant flaske', description: summary.title, tone: 'success' }
          : { title: 'Fant ikke strekkoden', description: barcode, tone: 'danger' },
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setResult({ kind: 'error', message });
      upsertBatchScan({
        barcode,
        status: 'error',
        title: 'Oppslag feilet',
        description: barcode,
        error: message,
        count: 1,
        updatedAt: Date.now(),
      });
      push({ title: 'Skann feilet', description: message, tone: 'danger' });
    } finally {
      resolvingCodesRef.current.delete(barcode);
      setBarcodePendingCount((count) => Math.max(0, count - 1));
    }
  };

  const onPhoto = async (file: File) => {
    photoCounterRef.current += 1;
    const stackId = `photo-${photoCounterRef.current}-${Date.now()}`;
    // Build a preview URL for the stack card
    let previewUrl: string | undefined;
    try {
      previewUrl = await fileToBase64(file);
    } catch {
      previewUrl = undefined;
    }
    setPhotoStack((prev) => [
      { id: stackId, status: 'scanning' as const, previewUrl },
      ...prev,
    ].slice(0, 20));
    scanLabel.mutate({ file, stackId });
  };

  const handlePhotoStackConfirm = async (
    stackId: string,
    product: NonNullable<BarcodeResolveResult['product']>,
    vintage: string | undefined,
  ) => {
    setAddingPhotoId(stackId);
    const country = (product.origins as { country?: string } | undefined)?.country;
    const region = (product.origins as { region?: string } | undefined)?.region;
    try {
      const bottle = await Bottles.create({
        productId: product.basic.productId,
        catalogId: product.basic.productId,
        name: product.basic.productLongName ?? product.basic.productShortName ?? 'Ukjent',
        vintage: vintage?.trim() || product.basic.vintage,
        country,
        region,
        photoUrl: product.imageUrl,
        qty: 1,
      });
      const labelQueued = await queueQrLabelsForBottles([bottle.id]);
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      setPhotoStack((prev) => prev.map((it) =>
        it.id === stackId ? { ...it, status: 'added', addedBottleId: bottle.id } : it,
      ));
      push({
        title: labelQueued ? 'Lagt i kjelleren og etikettkø' : 'Lagt i kjelleren',
        description: bottle.name,
        tone: 'success',
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      push({ title: 'Kunne ikke legge til', description: msg, tone: 'danger' });
    } finally {
      setAddingPhotoId(null);
    }
  };

  const openManualDrawer = (prefill: Record<string, unknown>, sourceStackId?: string) => {
    setDrawerPrefill(prefill as Partial<BottleCreate>);
    setDrawerSourceStackId(sourceStackId ?? null);
    setDrawerOpen(true);
  };

  const acceptBatchItem = async (item: BatchScanItem) => {
    const product = item.product ?? summarizeBarcodeResult(item.data).product;
    if (!product?.basic.productId) return;

    setAcceptingBarcode(item.barcode);
    const qty = Math.max(1, item.count);
    try {
      const data = await createBottleFromBarcode({
        barcode: item.barcode,
        productId: product.basic.productId,
        vintage: product.basic.vintage,
        qty,
      });
      const labelQueued = await queueQrLabelsForBottles(Array.from({ length: qty }, () => data.bottle.id));
      setBatchScans((current) => current.map((entry) => (
        entry.barcode === item.barcode
          ? { ...entry, status: 'accepted', title: data.bottle.name, product, updatedAt: Date.now() }
          : entry
      )));
      push({
        title: labelQueued ? 'Lagt i kjelleren og etikettkø' : 'Lagt i kjelleren',
        description: qty > 1 ? `${data.bottle.name} x${qty}` : data.bottle.name,
        tone: 'success',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setBatchScans((current) => current.map((entry) => (
        entry.barcode === item.barcode ? { ...entry, status: 'error', error: message, updatedAt: Date.now() } : entry
      )));
      push({ title: 'Kunne ikke legge til', description: message, tone: 'danger' });
    } finally {
      setAcceptingBarcode(null);
    }
  };

  const acceptAllFound = async () => {
    const items = batchScans.filter((item) => item.status === 'found' && (item.product ?? summarizeBarcodeResult(item.data).product));
    for (const item of items) {
      // Sequential writes keep mapping conflicts and toast feedback readable.
      // eslint-disable-next-line no-await-in-loop
      await acceptBatchItem(item);
    }
  };

  const removeBatchItem = (barcode: string) => {
    setBatchScans((current) => current.filter((item) => item.barcode !== barcode));
  };

  return (
    <>
    <div className="grid gap-6 lg:grid-cols-[minmax(320px,520px)_1fr]">
      {/* ── Left / top column: scanner + photo stack (continuous) ── */}
      <section className="grid gap-4">
        <header>
          <h1 className="ui-section-title text-3xl">{t('scan.title')}</h1>
          <p className="text-sm text-mist">{t('scan.subtitle')}</p>
        </header>
        <Tabs<ScannerMode>
          tabs={[
            { id: 'auto', label: `${t('scan.barcode')} / ${t('scan.qr')}`, icon: 'barcode' },
            { id: 'photo', label: t('scan.photo'), icon: 'camera' },
          ]}
          active={mode}
          onChange={(id) => {
            setMode(id);
            setResult({ kind: 'idle' });
          }}
        />
        {/* Scanner — always fixed at top, never scrolled away */}
        <UnifiedScanner mode={mode} continuous={mode === 'auto'} onCode={onCode} onPhoto={onPhoto} />

        <LabelQueueOption
          enabled={labelQueueEnabled}
          printerName={printTarget?.printerName}
          onChange={setLabelQueueEnabled}
        />

        {/* ── Continuous photo-scan stack ── */}
        {mode === 'photo' && (
          <div className="grid gap-3">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-base text-parchment">Skannet etiketter</h2>
              {photoStack.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPhotoStack([])}
                  className="text-[11px] uppercase tracking-widest text-mist hover:text-candle"
                >
                  Tøm
                </button>
              )}
            </div>
            {photoStack.length === 0 ? (
              <div className="rounded-md border border-dashed border-line p-4 text-center text-xs text-mist">
                Skannede etiketter dukker opp her. Skann flaske etter flaske uten å bla ned.
              </div>
            ) : (
              <div className="grid gap-2">
                <AnimatePresence initial={false}>
                  {photoStack.map((item) => (
                    <ScanResultCard
                      key={item.id}
                      data={item}
                      labelQueueEnabled={labelQueueEnabled}
                      onConfirmMatch={(product, vintage) => handlePhotoStackConfirm(item.id, product, vintage)}
                      onManualAdd={(prefill) => openManualDrawer(
                        item.scanData ? extractionToPrefill(item.scanData.extraction) : prefill,
                        item.id,
                      )}
                      onDismiss={() => setPhotoStack((prev) => prev.filter((it) => it.id !== item.id))}
                      confirmingProductId={addingPhotoId === item.id
                        ? (item.scanData?.candidates[0]?.product.basic.productId ?? null)
                        : null}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}

        {/* ── Barcode batch scan queue (barcode/auto mode) ── */}
        {mode === 'auto' && (
          <BatchScanQueue
            items={batchScans}
            pendingCount={barcodePendingCount}
            acceptingBarcode={acceptingBarcode}
            onAccept={acceptBatchItem}
            onAcceptAll={acceptAllFound}
            onRemove={removeBatchItem}
            onClear={() => setBatchScans([])}
            labelQueueEnabled={labelQueueEnabled}
          />
        )}

        {/* ── Bulk links ── */}
        <div className="grid gap-2">
          <Link
            to="/scan/receipt"
            className="ui-card ui-card--interactive flex items-center gap-3 p-4"
          >
            <Icon name="receipt" className="text-candle" />
            <div className="grid flex-1">
              <span className="font-serif text-sm text-parchment">Bulk fra kvittering</span>
              <span className="text-xs text-mist">
                Lim inn en butikkvittering, vi finner og legger til flaskene i én operasjon
              </span>
            </div>
            <Icon name="chevron-right" className="text-mist" />
          </Link>
          <Link
            to="/scan/labels-bulk"
            className="ui-card ui-card--interactive flex items-center gap-3 p-4"
          >
            <Icon name="camera" className="text-candle" />
            <div className="grid flex-1">
              <span className="font-serif text-sm text-parchment">Bulk etikettskanning</span>
              <span className="text-xs text-mist">
                Velg eller slipp mange etikettbilder — AI leser og finner katalogtref for hvert
              </span>
            </div>
            <Icon name="chevron-right" className="text-mist" />
          </Link>
        </div>
      </section>

      {/* ── Right / bottom column: barcode result detail (legacy panel) ── */}
      <section className="grid gap-4">
        <h2 className="ui-section-title text-2xl">{t('scan.resolveTitle')}</h2>

        {(barcodePendingCount > 0 || scanLabel.isPending) && (
          <Card className="grid gap-3 p-6">
            <div className="flex items-center gap-3">
              <Spinner />
              <p className="text-sm text-parchment">{t('common.loading')}</p>
            </div>
            <Skeleton height={120} />
            <Skeleton height={16} width="60%" />
            <Skeleton height={16} width="40%" />
          </Card>
        )}

        {result.kind === 'idle' && barcodePendingCount === 0 && !scanLabel.isPending && (
          <Card className="p-8">
            <EmptyState
              icon="scan"
              title="Klar til å skanne"
              description="Bruk kameraet eller last opp et bilde av etiketten."
            />
          </Card>
        )}

        {result.kind === 'error' && (
          <Card className="p-6">
            <EmptyState icon="warning" title={t('common.error')} description={result.message} />
          </Card>
        )}

        {result.kind === 'barcode' && (
          <BarcodeResult
            key={result.data.barcode.input}
            data={result.data}
            onConfirm={(productId, vintage) =>
              confirmBarcode.mutate({ barcode: result.data.barcode.input, productId, vintage })
            }
            confirming={confirmBarcode.isPending}
            labelQueueEnabled={labelQueueEnabled}
          />
        )}

        {result.kind === 'photo' && (
          <PhotoResult
            data={result.data}
            onConfirm={(product, vintage) => confirmPhotoCandidate.mutate({ product, vintage })}
            confirming={confirmPhotoCandidate.isPending}
            confirmingProductId={confirmPhotoCandidate.variables?.product.basic.productId ?? null}
            labelQueueEnabled={labelQueueEnabled}
            onManualAdd={(prefill) => openManualDrawer(prefill)}
          />
        )}
      </section>
    </div>

    <AddBottleDrawer
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      prefill={drawerPrefill}
      onAdded={(bottleId) => {
        void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
        // Mark the originating photo-stack card as added
        if (drawerSourceStackId) {
          setPhotoStack((prev) =>
            prev.map((it) =>
              it.id === drawerSourceStackId
                ? { ...it, status: 'added', addedBottleId: bottleId }
                : it,
            ),
          );
          setDrawerSourceStackId(null);
        }
        setDrawerOpen(false);
        // Navigate only when there's no active photo stack session
        navigate(`/bottle/${bottleId}`);
      }}
    />
    </>
  );
}

function LabelQueueOption({
  enabled,
  printerName,
  onChange,
}: {
  enabled: boolean;
  printerName?: string;
  onChange: (enabled: boolean) => void;
}) {
  return (
    <Card className="flex flex-wrap items-center justify-between gap-3 p-3">
      <div className="flex min-w-0 items-center gap-3">
        <Icon name="tag" className="text-candle" size={18} />
        <div className="grid min-w-0">
          <span className="font-serif text-sm text-parchment">QR-etikett ved innlegging</span>
          <span className="truncate text-xs text-mist">
            {printerName ? `Kø til ${printerName}` : 'Legges i etikettkø for print'}
          </span>
        </div>
      </div>
      <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-parchment">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => onChange(event.target.checked)}
          className="h-4 w-4 accent-[color:var(--ui-color-candle)]"
        />
        Aktiv
      </label>
    </Card>
  );
}

function BatchScanQueue({
  items,
  pendingCount,
  acceptingBarcode,
  onAccept,
  onAcceptAll,
  onRemove,
  onClear,
  labelQueueEnabled,
}: {
  items: BatchScanItem[];
  pendingCount: number;
  acceptingBarcode: string | null;
  onAccept: (item: BatchScanItem) => void | Promise<void>;
  onAcceptAll: () => void | Promise<void>;
  onRemove: (barcode: string) => void;
  onClear: () => void;
  labelQueueEnabled: boolean;
}) {
  const acceptableCount = items.filter((item) => item.status === 'found' && (item.product ?? summarizeBarcodeResult(item.data).product)).length;

  return (
    <Card className="grid gap-3 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-lg text-parchment">Batch-skanning</h2>
          <p className="text-xs text-mist">
            {items.length ? `${items.length} skannet · ${acceptableCount} klar til å legges inn` : 'Skann flere flasker fortløpende. Treff legges her.'}
          </p>
        </div>
        {pendingCount > 0 && <Badge tone="gold">{pendingCount} søker</Badge>}
      </div>

      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={onAcceptAll} disabled={!acceptableCount || Boolean(acceptingBarcode)}>
            {labelQueueEnabled ? 'Legg til alle treff + QR' : 'Legg til alle treff'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear} disabled={Boolean(acceptingBarcode)}>
            Tøm liste
          </Button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-line p-3 text-xs text-mist">
          Kameraet kan stå åpent i strekkodemodus. Du får toast for funnet/ikke funnet uten å måtte gå ned til resultatpanelet.
        </div>
      ) : (
        <div className="grid max-h-[24rem] gap-2 overflow-auto pr-1">
          {items.map((item) => {
            const product = item.product ?? summarizeBarcodeResult(item.data).product;
            const tone = item.status === 'accepted'
              ? 'success'
              : item.status === 'found'
                ? 'gold'
                : item.status === 'pending'
                  ? 'mist'
                  : 'danger';
            return (
              <div key={item.barcode} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-md border border-line p-3">
                <div className="relative grid h-12 w-9 place-items-center overflow-hidden rounded bg-ink-soft">
                  {product ? (
                    <BottleImage src={product.imageUrl} iconSize={16} />
                  ) : (
                    <Icon name={item.status === 'pending' ? 'spinner' : 'barcode'} className={item.status === 'pending' ? 'animate-spin text-mist' : 'text-mist'} />
                  )}
                </div>
                <div className="grid min-w-0 gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={tone}>{batchStatusLabel(item.status)}</Badge>
                    {item.count > 1 && <span className="text-[10px] uppercase tracking-widest text-mist">x{item.count}</span>}
                  </div>
                  <strong className="truncate font-serif text-sm text-parchment">{item.title}</strong>
                  <span className="truncate text-xs text-mist">{item.error || item.description || item.barcode}</span>
                </div>
                <div className="flex items-center gap-1">
                  {product && item.status !== 'accepted' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onAccept(item)}
                      loading={acceptingBarcode === item.barcode}
                    >
                      {labelQueueEnabled ? 'Legg til + QR' : 'Legg til'}
                    </Button>
                  )}
                  <button
                    type="button"
                    className="ui-btn ui-btn--icon"
                    aria-label="Fjern fra batch"
                    onClick={() => onRemove(item.barcode)}
                    disabled={acceptingBarcode === item.barcode}
                  >
                    <Icon name="x" size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function summarizeBarcodeResult(data?: BarcodeResolveResult): { product?: BarcodeProduct; title: string; description: string } {
  const product = data?.product ?? data?.candidates[0]?.product;
  if (product) {
    return {
      product,
      title: productDisplayName(product),
      description: [
        product.basic.vintage,
        (product.origins as { country?: string } | undefined)?.country,
        `#${product.basic.productId}`,
      ].filter(Boolean).join(' · '),
    };
  }

  const providerResult = data?.providerResults.find((entry) => entry.title || entry.brand);
  if (providerResult) {
    return {
      title: providerResult.title ?? providerResult.brand ?? 'Produktinfo funnet',
      description: 'Fant ekstern barcode-info, men ingen trygg katalogmatch.',
    };
  }

  return {
    title: 'Ingen match',
    description: data ? `Strekkode ${data.barcode.input}` : 'Oppslag mangler resultat.',
  };
}

function productDisplayName(product: BarcodeProduct): string {
  return product.basic.productLongName ?? product.basic.productShortName ?? 'Ukjent produkt';
}

function batchStatusLabel(status: BatchScanStatus): string {
  return {
    pending: 'Søker',
    found: 'Funnet',
    not_found: 'Ikke funnet',
    error: 'Feil',
    accepted: 'Lagt inn',
  }[status];
}

function BarcodeResult({
  data,
  onConfirm,
  confirming,
  labelQueueEnabled,
}: {
  data: BarcodeResolveResult;
  onConfirm: ConfirmBarcodeSelection;
  confirming: boolean;
  labelQueueEnabled: boolean;
}) {
  if (data.status === 'mapped' && data.product) {
    return (
      <MappedBarcodeCard
        product={data.product}
        onConfirm={onConfirm}
        confirming={confirming}
        labelQueueEnabled={labelQueueEnabled}
      />
    );
  }

  const [best, ...alternatives] = data.candidates;
  const providerResult = data.providerResults.find((p) => p.title || p.brand || p.imageUrl);

  return (
    <Card className="grid gap-4 p-6">
      <div>
        <h3 className="ui-section-title text-xl">Velg riktig produkt</h3>
        <p className="text-xs text-mist">Strekkode {data.barcode.input} ({data.barcode.gtin14})</p>
      </div>
      {!best ? (
        <div className="grid gap-4">
          {providerResult && <ExternalBarcodeInfo result={providerResult} />}
          <EmptyState
            icon="bottle-empty"
            title={providerResult ? 'Ingen trygg katalogmatch' : 'Ingen match'}
            description={
              providerResult
                ? 'Barcode-kilden fant produktinfo, men Vinmonopolet-katalogen ga ingen trygge treff. Ta bilde av etiketten eller søk manuelt.'
                : 'Strekkoden finnes ikke hos aktiverte barcode-kilder. Ta bilde av etiketten eller søk manuelt.'
            }
          />
        </div>
      ) : (
        <div className="grid gap-2">
          <BestCandidateCard
            candidate={best}
            onConfirm={onConfirm}
            confirming={confirming}
            labelQueueEnabled={labelQueueEnabled}
          />
          {alternatives.length > 0 && (
            <div className="mt-2 grid gap-2">
              <span className="text-[10px] uppercase tracking-widest text-mist">Andre mulige treff</span>
              {alternatives.map((c) => (
                <CandidateButton
                  key={c.product.basic.productId}
                  candidate={c}
                  onConfirm={onConfirm}
                  confirming={confirming}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function MappedBarcodeCard({
  product,
  onConfirm,
  confirming,
  labelQueueEnabled,
}: {
  product: BarcodeProduct;
  onConfirm: ConfirmBarcodeSelection;
  confirming: boolean;
  labelQueueEnabled: boolean;
}) {
  const navigate = useNavigate();
  const productName = product.basic.productShortName ?? product.basic.productLongName;
  const [vintage, setVintage] = useState(product.basic.vintage ?? '');

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
      <Card glow className="grid gap-4 p-6">
        <div className="flex items-center gap-3">
          <Icon name="check-circle" className="text-[color:var(--ui-color-success)]" size={22} />
          <h3 className="ui-section-title text-xl">Vi kjenner denne flasken</h3>
        </div>
        <ProductHero product={product} badge="Bekreftet strekkode" />
        <Input
          label="Årgang"
          inputMode="numeric"
          placeholder="f.eks. 2020"
          value={vintage}
          onChange={(e) => setVintage(e.target.value)}
          hint={
            product.basic.vintage
              ? 'Årgangen kommer fra katalogen. Endre hvis flasken du holder er en annen årgang.'
              : 'Strekkoden sier ofte bare produktet, ikke årgangen. Sett årgang før du legger den inn.'
          }
        />
        <div className="grid gap-2 sm:flex sm:items-center sm:justify-between">
          <Button
            variant="primary"
            iconLeft="add-bottle"
            onClick={() => onConfirm(product.basic.productId, vintage)}
            loading={confirming}
            className="sm:flex-1"
          >
            {labelQueueEnabled ? 'Legg i kjelleren + QR' : 'Legg i kjelleren'}
          </Button>
          <Button
            variant="ghost"
            iconRight="arrow-right"
            onClick={() => navigate(`/bottle/${product.basic.productId}`)}
          >
            Detaljer
          </Button>
        </div>
        {productName && (
          <Link
            to={`/cellar?q=${encodeURIComponent(productName)}`}
            className="text-xs uppercase tracking-widest text-mist hover:text-candle"
          >
            Ikke riktig flaske? Søk etter andre varianter →
          </Link>
        )}
      </Card>
    </motion.div>
  );
}

function BestCandidateCard({
  candidate,
  onConfirm,
  confirming,
  labelQueueEnabled,
}: {
  candidate: BarcodeCandidate;
  onConfirm: ConfirmBarcodeSelection;
  confirming: boolean;
  labelQueueEnabled: boolean;
}) {
  const [vintage, setVintage] = useState(candidate.product.basic.vintage ?? '');

  return (
    <div className="grid gap-4 rounded-md border border-candle/40 bg-candle/5 p-4">
      <div className="flex items-center justify-between gap-3">
        <Badge tone="gold">Beste treff</Badge>
        <span className="text-xs text-mist">{candidateMatchLabel(candidate)}</span>
      </div>
      <ProductHero product={candidate.product} badge={candidateMatchLabel(candidate)} />
      <p className="text-xs text-mist">{candidateMatchHint(candidate)}</p>
      <Input
        label="Årgang"
        inputMode="numeric"
        placeholder="f.eks. 2020"
        value={vintage}
        onChange={(e) => setVintage(e.target.value)}
        hint={
          candidate.product.basic.vintage
            ? 'Årgangen kommer fra katalogen. Endre hvis flasken du holder er en annen årgang.'
            : 'Strekkoden peker ofte på produktet uten årgang. Sett årgang før du legger den inn.'
        }
      />
      <Button
        variant="primary"
        iconLeft="add-bottle"
        onClick={() => onConfirm(candidate.product.basic.productId, vintage)}
        loading={confirming}
      >
        {labelQueueEnabled ? 'Legg beste treff i kjelleren + QR' : 'Legg beste treff i kjelleren'}
      </Button>
    </div>
  );
}

function CandidateButton({
  candidate,
  onConfirm,
  confirming,
}: {
  candidate: BarcodeCandidate;
  onConfirm: ConfirmBarcodeSelection;
  confirming: boolean;
}) {
  return (
            <button
              type="button"
      onClick={() => onConfirm(candidate.product.basic.productId, candidate.product.basic.vintage)}
              disabled={confirming}
      className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-md border border-line p-3 text-left transition-colors hover:border-line-soft disabled:opacity-60"
            >
              <div className="relative grid h-12 w-9 place-items-center overflow-hidden rounded bg-ink-soft">
        <BottleImage src={candidate.product.imageUrl} iconSize={16} />
              </div>
              <div className="grid">
                <span className="font-serif text-sm text-parchment">
          {candidate.product.basic.productLongName ?? candidate.product.basic.productShortName}
                </span>
        <span className="text-xs text-mist">{candidateMatchLabel(candidate)} · {candidateMatchHint(candidate)}</span>
              </div>
              <Icon name="chevron-right" className="text-mist" />
            </button>
  );
}

function ProductHero({ product, badge }: { product: BarcodeProduct; badge?: string }) {
  const name = product.basic.productLongName ?? product.basic.productShortName ?? 'Ukjent produkt';
  const country = (product.origins as { country?: string } | undefined)?.country;
  const region = (product.origins as { region?: string } | undefined)?.region;
  const price = (product.prices as { salesPrice?: number } | undefined)?.salesPrice;

  return (
    <div className="grid gap-4 sm:grid-cols-[120px_1fr] sm:items-center xl:grid-cols-[140px_1fr]">
      <div className="relative grid h-40 place-items-center overflow-hidden rounded-md bg-ink-soft p-3 sm:h-36 xl:h-40">
        <BottleImage src={product.imageUrl} alt={name} iconSize={56} mode="lightbox" />
      </div>
      <div className="grid min-w-0 gap-2">
        <div className="flex flex-wrap gap-2">
          {badge && <Badge tone="gold">{badge}</Badge>}
          {product.basic.vintage && <Badge tone="ruby">{product.basic.vintage}</Badge>}
          {country && <Badge tone="mist">{country}</Badge>}
        </div>
        <h4 className="font-serif text-2xl text-parchment">{name}</h4>
        <p className="text-sm text-mist">
          {[region, country, product.basic.vintage].filter(Boolean).join(' · ') || 'Årgang/område ikke oppgitt'}
        </p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-mist">
          <span>#{product.basic.productId}</span>
          {price != null && <span>{formatPriceNok(price)}</span>}
        </div>
      </div>
    </div>
  );
}

function ExternalBarcodeInfo({ result }: { result: BarcodeResolveResult['providerResults'][number] }) {
  const title = result.title ?? result.brand ?? 'Produktinfo fra barcode-kilde';
  return (
    <div className="grid gap-3 rounded-md border border-line p-3 sm:grid-cols-[84px_1fr] sm:items-center xl:grid-cols-[96px_1fr]">
      <div className="relative grid h-28 place-items-center overflow-hidden rounded-md bg-ink-soft p-2 sm:h-24 xl:h-28">
        <BottleImage src={result.imageUrl} alt={title} iconSize={34} mode="lightbox" />
      </div>
      <div className="grid gap-1">
        <Badge tone="mist">{result.provider}</Badge>
        <h4 className="font-serif text-lg text-parchment">{title}</h4>
        {result.brand && <p className="text-sm text-mist">{result.brand}</p>}
        <p className="text-xs text-mist">Fant barcode-info, men ikke en trygg katalogkandidat.</p>
      </div>
    </div>
  );
}

function candidateMatchLabel(candidate: MatchCandidate): string {
  if (!Number.isFinite(candidate.score)) return 'Navnetreff';
  return `Navnetreff ${Math.round(candidate.score * 100)}%`;
}

function candidateMatchHint(candidate: MatchCandidate): string {
  return candidate.matchedOn.length
    ? `Matchet på navn/merke: ${candidate.matchedOn.join(', ')}`
    : 'Forslag basert på produktnavn fra barcode-kilden.';
}

function PhotoResult({
  data,
  onConfirm,
  confirming,
  confirmingProductId,
  labelQueueEnabled,
  onManualAdd,
}: {
  data: ScanResponse;
  onConfirm: ConfirmProductSelection;
  confirming: boolean;
  confirmingProductId: string | null;
  labelQueueEnabled: boolean;
  onManualAdd?: (prefill: Record<string, unknown>) => void;
}) {
  const ext = data.extraction;
  const [selectedCandidate, setSelectedCandidate] = useState<PhotoCandidate | null>(null);

  const handleManualAdd = () => {
    if (!onManualAdd) return;
    onManualAdd(extractionToPrefill(ext));
  };

  return (
    <Card className="grid gap-4 p-6">
      <div className="flex items-center gap-3">
        <Icon name="sparkles" className="text-candle" size={22} />
        <h3 className="ui-section-title text-xl">AI-tolket etikett</h3>
        {ext.confidence != null && <Badge tone="gold">conf {(ext.confidence * 100).toFixed(0)}%</Badge>}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Produkt" value={ext.productName} />
        <Field label="Produsent" value={ext.producer} />
        <Field label="Årgang" value={ext.vintage} />
        <Field label="Område" value={ext.region} />
        <Field label="Land" value={ext.country} />
        <Field label="Drue" value={ext.grape} />
      </div>

      <h4 className="font-serif text-lg text-parchment">Treff i katalogen</h4>
      {data.candidates.length === 0 ? (
        <div className="grid gap-3">
          <EmptyState icon="bottle-empty" title="Ingen treff" description="Prøv et bedre bilde, eller legg til manuelt." />
          {onManualAdd && (
            <Button
              variant="primary"
              iconLeft="add-bottle"
              onClick={handleManualAdd}
              className="w-full sm:w-auto"
            >
              Legg til manuelt
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-2">
          {data.candidates.slice(0, 6).map((c) => (
            <ProductRow
              key={c.product.basic.productId}
              product={c.product}
              matchLabel={candidateMatchLabel(c)}
              matchHint={candidateMatchHint(c)}
              onInspect={() => setSelectedCandidate(c)}
              onConfirm={(product) => onConfirm(product, ext.vintage)}
              confirming={confirmingProductId === c.product.basic.productId}
              disabled={confirming && confirmingProductId !== c.product.basic.productId}
              labelQueueEnabled={labelQueueEnabled}
            />
          ))}
          {/* Fallback when none of the catalog matches are right */}
          {onManualAdd && (
            <div className="flex items-center justify-end gap-2 pt-1">
              <span className="text-xs text-mist">Ingen passer?</span>
              <Button size="sm" variant="ghost" iconLeft="edit" onClick={handleManualAdd}>
                Legg til manuelt
              </Button>
            </div>
          )}
        </div>
      )}
      <ProductDetailModal
        candidate={selectedCandidate}
        extractionVintage={ext.vintage}
        onClose={() => setSelectedCandidate(null)}
        onConfirm={onConfirm}
        confirming={confirming}
        labelQueueEnabled={labelQueueEnabled}
      />
    </Card>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-mist">{label}</div>
      <div className="text-sm text-parchment">{value ?? '—'}</div>
    </div>
  );
}

function ProductRow({
  product,
  matchLabel,
  matchHint,
  onInspect,
  onConfirm,
  confirming = false,
  disabled = false,
  labelQueueEnabled = false,
}: {
  product: BarcodeProduct;
  matchLabel?: string;
  matchHint?: string;
  onInspect?: () => void;
  onConfirm?: ConfirmProductSelection;
  confirming?: boolean;
  disabled?: boolean;
  labelQueueEnabled?: boolean;
}) {
  const price = (product.prices as { salesPrice?: number } | undefined)?.salesPrice;
  const country = (product.origins as { country?: string } | undefined)?.country;
  const region = (product.origins as { region?: string } | undefined)?.region;
  return (
    <div className="ui-card grid grid-cols-[auto_1fr_auto] items-center gap-3 p-3">
      <button
        type="button"
        onClick={onInspect}
        disabled={!onInspect}
        className="relative grid h-14 w-10 place-items-center overflow-hidden rounded bg-ink-soft disabled:cursor-default"
        aria-label={onInspect ? `Vis større bilde av ${productDisplayName(product)}` : undefined}
      >
        <BottleImage src={product.imageUrl} iconSize={16} />
      </button>
      <div className="grid min-w-0 gap-1">
        <span className="font-serif text-sm text-parchment">
          {product.basic.productLongName ?? product.basic.productShortName}
        </span>
        <span className="text-xs text-mist">
          {[product.basic.vintage, region ?? country, `#${product.basic.productId}`].filter(Boolean).join(' · ')}
        </span>
        {(matchLabel || matchHint) && (
          <span className="line-clamp-1 text-[11px] text-mist">
            {[matchLabel, matchHint].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
      <div className="grid justify-items-end gap-2">
        <Badge tone="gold">{formatPriceNok(price)}</Badge>
        {onInspect && (
          <Button size="sm" variant="ghost" iconLeft="eye" onClick={onInspect}>
            Detaljer
          </Button>
        )}
        {onConfirm && (
          <Button
            size="sm"
            variant="ghost"
            iconLeft="add-bottle"
            onClick={() => onConfirm(product, product.basic.vintage)}
            loading={confirming}
            disabled={disabled}
          >
            {labelQueueEnabled ? 'Legg til + QR' : 'Legg til'}
          </Button>
        )}
      </div>
    </div>
  );
}

function ProductDetailModal({
  candidate,
  extractionVintage,
  onClose,
  onConfirm,
  confirming,
  labelQueueEnabled,
}: {
  candidate: PhotoCandidate | null;
  extractionVintage?: string;
  onClose: () => void;
  onConfirm: ConfirmProductSelection;
  confirming: boolean;
  labelQueueEnabled: boolean;
}) {
  if (!candidate) return null;

  const product = candidate.product;
  const name = productDisplayName(product);
  const country = (product.origins as { country?: string } | undefined)?.country;
  const region = (product.origins as { region?: string } | undefined)?.region;
  const price = (product.prices as { salesPrice?: number } | undefined)?.salesPrice;
  const category = productCategoryLabel(product);
  const producer = productProducer(product);
  const grapes = productGrapes(product);
  const foodPairings = productFoodPairings(product);
  const tastingRows = productTastingRows(product);
  const confirmVintage = extractionVintage?.trim() || product.basic.vintage;
  const details = [
    { label: 'Match', value: candidateMatchLabel(candidate) },
    { label: 'Katalog-ID', value: `#${product.basic.productId}` },
    { label: 'Pris', value: formatPriceNok(price) },
    { label: 'Årgang', value: product.basic.vintage },
    { label: 'Etikett-årgang', value: extractionVintage },
    { label: 'Volum', value: formatVolumeMl(product.basic.volume) },
    { label: 'Alkohol', value: formatAbv(product.basic.alcoholContent) },
    { label: 'Kategori', value: category },
    { label: 'Produsent', value: producer },
    { label: 'Område', value: [region, country].filter(Boolean).join(', ') },
  ].filter((row) => row.value);

  return (
    <Modal
      open={Boolean(candidate)}
      onClose={onClose}
      title="Detaljer om treff"
      size="lg"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Lukk</Button>
          <Button
            variant="primary"
            iconLeft="add-bottle"
            loading={confirming}
            onClick={() => onConfirm(product, confirmVintage)}
          >
            {labelQueueEnabled ? 'Legg i kjelleren + QR' : 'Legg i kjelleren'}
          </Button>
        </>
      )}
    >
      <div className="grid gap-6 md:grid-cols-[minmax(170px,240px)_1fr]">
        <div className="relative grid h-[22rem] place-items-center overflow-hidden rounded-md bg-ink-soft p-4 md:h-[24rem]">
          <BottleImage src={product.imageUrl} alt={name} iconSize={88} mode="lightbox" />
        </div>

        <div className="grid min-w-0 gap-4">
          <div className="grid gap-2">
            <div className="flex flex-wrap gap-2">
              {product.basic.vintage && <Badge tone="ruby">{product.basic.vintage}</Badge>}
              {country && <Badge tone="mist">{country}</Badge>}
              {category && <Badge tone="gold">{category}</Badge>}
            </div>
            <h3 className="font-serif text-2xl leading-tight text-parchment">{name}</h3>
            <p className="text-sm text-mist">{candidateMatchHint(candidate)}</p>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {details.map((row) => (
              <Field key={row.label} label={row.label} value={row.value} />
            ))}
          </div>

          {grapes.length > 0 && (
            <div className="grid gap-2">
              <div className="text-[10px] uppercase tracking-widest text-mist">Druer</div>
              <div className="flex flex-wrap gap-2">
                {grapes.map((grape) => <Badge key={grape} tone="ruby">{grape}</Badge>)}
              </div>
            </div>
          )}

          {foodPairings.length > 0 && (
            <div className="grid gap-2">
              <div className="text-[10px] uppercase tracking-widest text-mist">Mat</div>
              <div className="flex flex-wrap gap-2">
                {foodPairings.map((pairing) => <Badge key={pairing} tone="mist">{pairing}</Badge>)}
              </div>
            </div>
          )}
        </div>
      </div>

      {tastingRows.length > 0 && (
        <div className="mt-6 grid gap-3 rounded-md border border-line p-4">
          <h4 className="font-serif text-lg text-parchment">Smaksinfo</h4>
          <div className="grid gap-3 sm:grid-cols-2">
            {tastingRows.map((row) => (
              <Field key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function productProducer(product: BarcodeProduct): string | undefined {
  const properties = asRecord(product.properties);
  return firstString(properties.producer, properties.manufacturer, properties.vendor);
}

function productCategoryLabel(product: BarcodeProduct): string | undefined {
  const classification = asRecord(product.classification);
  const main = labelFromValue(classification.mainCategory ?? classification.main_category);
  const sub = labelFromValue(
    classification.mainSubCategory
      ?? classification.main_sub_category
      ?? classification.subCategory
      ?? classification.sub_category,
  );
  return [main, sub].filter(Boolean).join(' · ') || undefined;
}

function productGrapes(product: BarcodeProduct): string[] {
  const classification = asRecord(product.classification);
  const ingredients = asRecord(product.ingredients);
  return uniqueStrings([
    ...stringList(classification.grapeVarieties),
    ...stringList(classification.grapes),
    ...stringList(ingredients.grapeVarieties),
    ...stringList(ingredients.grapes),
  ]);
}

function productFoodPairings(product: BarcodeProduct): string[] {
  const description = asRecord(product.description);
  const properties = asRecord(product.properties);
  return uniqueStrings([
    ...stringList(description.foodPairings),
    ...stringList(description.food_pairings),
    ...stringList(description.food),
    ...stringList(properties.foodPairings),
    ...stringList(properties.food_pairings),
  ]).slice(0, 8);
}

function productTastingRows(product: BarcodeProduct): Array<{ label: string; value: string }> {
  const description = asRecord(product.description);
  const properties = asRecord(product.properties);
  const attributes = asRecord(product.attributes);
  return [
    { label: 'Farge', value: firstString(description.colour, description.color, properties.colour, properties.color) },
    { label: 'Aroma', value: firstString(description.odour, description.odor, description.aroma, properties.aroma) },
    { label: 'Smak', value: firstString(description.taste, description.flavour, description.flavor, properties.taste) },
    { label: 'Sødme', value: firstString(description.sweetness, properties.sweetness) },
    { label: 'Friskhet', value: firstString(description.freshness, properties.freshness) },
    { label: 'Fylde', value: firstString(description.fullness, properties.fullness) },
    { label: 'Garvestoff', value: firstString(description.tannins, properties.tannins) },
    { label: 'Lagring', value: firstString(properties.storagePotential, attributes.storagePotential) },
  ].filter((row): row is { label: string; value: string } => Boolean(row.value));
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    const label = labelFromValue(value);
    if (label) return label;
  }
  return undefined;
}

function labelFromValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const record = asRecord(value);
  return firstString(record.name, record.label, record.value, record.formattedValue, record.code);
}

function stringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      const label = labelFromValue(entry);
      return label ? [label] : [];
    });
  }
  const label = labelFromValue(value);
  return label ? label.split(/[;,]/).map((part) => part.trim()).filter(Boolean) : [];
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = value.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
